import { cn } from "@/utils/cn.ts";
import { CheckCircle2, LetterText, Pencil } from "lucide-react";
import { useTranslationDefaultResolver } from "./use-translation-defaults.ts";
import { PortalWordingDrawer } from "./portal-wording-drawer.tsx";
import type { WidgetKeyDescriptor } from "./widget-keys.ts";
import type { PortalCustomTextEntry } from "./types.ts";

type CustomTextEntry = PortalCustomTextEntry;

type Props = {
  descriptor: WidgetKeyDescriptor;
  override?: CustomTextEntry;
  status: "none" | "partial" | "full";
  disabled?: string | boolean;
};

export function PortalWordingItem({
  descriptor,
  override,
  status,
  disabled,
}: Props) {
  const { resolve } = useTranslationDefaultResolver("en");
  const defaultText = resolve(descriptor.key) ?? descriptor.label;

  const previewText =
    override?.texts?.find((t) => (t.text ?? "").trim().length > 0)?.text ??
    defaultText;

  return (
    <PortalWordingDrawer
      descriptor={descriptor}
      override={override}
      disabled={!!disabled}
    >
      <button
        type="button"
        disabled={!!disabled}
        className={cn(
          "w-full border border-border rounded-md items-center gap-3 px-3 py-2 flex text-left",
          !disabled && "hover:bg-accent transition-colors",
          disabled && "cursor-not-allowed opacity-40",
        )}
      >
        <StatusIcon status={status} />
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <span className="text-sm font-medium shrink-0">
            {descriptor.label}
          </span>
          <code className="text-[10px] font-mono text-muted-foreground bg-muted px-1.5 py-0.5 rounded shrink-0">
            {descriptor.key}
          </code>
          {descriptor.widgets.length === 1 && (
            <span className="text-[10px] text-muted-foreground border border-border px-1.5 py-0.5 rounded shrink-0">
              {descriptor.widgets[0] === "legacy" ? "Ancien portail" : "Nouveau portail"}
            </span>
          )}
          <span
            className={cn(
              "text-xs truncate",
              status === "none"
                ? "text-muted-foreground italic"
                : "text-foreground",
            )}
          >
            {previewText}
          </span>
        </div>
        <Pencil className="size-3.5 text-muted-foreground shrink-0" />
      </button>
    </PortalWordingDrawer>
  );
}

function StatusIcon({ status }: { status: Props["status"] }) {
  if (status === "full")
    return <CheckCircle2 className="text-green-500 size-5 shrink-0" />;
  if (status === "partial")
    return <CheckCircle2 className="text-yellow-500 size-5 shrink-0" />;
  return <LetterText className="text-muted-foreground size-5 shrink-0" />;
}
